import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CalendarClock, FileText, ListChecks, Lock } from "lucide-react";
import api, { formatApiError } from "../api";
import i18n from "../i18n";
import { Badge, EmptyState, Pagination, Select, Spinner, Toast } from "../components/common";
import TranscriptDropzone from "../components/TranscriptDropzone";

const PAGE_SIZE = 25;
const STATES = ["", "draft", "processing", "ready", "failed"];

function fmtDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(i18n.language, {
    year: "numeric", month: "short", day: "numeric",
  });
}

export default function Meetings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [state, setState] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/meetings", {
        params: { state, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
      });
      setItems(r.data);
      setTotal(Number(r.headers["x-total-count"] || r.data.length));
    } catch (e) {
      setItems([]);
      setToast({ type: "error", message: formatApiError(e.response?.data?.detail) || e.message });
    }
  }, [state, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
          {t("meet.title")}
        </h1>
        <div className="w-40">
          <Select
            data-testid="meet-state-filter"
            value={state}
            onChange={(e) => { setPage(1); setState(e.target.value); }}
          >
            {STATES.map((s) => (
              <option key={s || "all"} value={s}>
                {s ? t(`meet.state.${s}`) : t("meet.allStates")}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <TranscriptDropzone
        onUploaded={(m) => navigate(`/meetings/${m.id}`)}
      />

      {items === null ? (
        <Spinner />
      ) : items.length === 0 ? (
        <EmptyState title={t("meet.emptyTitle")} hint={t("meet.emptyHint")} />
      ) : (
        <>
          <div className="border border-border rounded-sm divide-y divide-border" data-testid="meet-list">
            {items.map((m) => (
              <button
                key={m.id}
                data-testid={`meet-row-${m.id}`}
                onClick={() => navigate(`/meetings/${m.id}`)}
                className="w-full text-left px-4 py-3.5 hover:bg-surface transition-colors flex items-start gap-3"
              >
                <CalendarClock size={18} className="text-muted mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{m.subject}</span>
                    {m.sensitive && <Lock size={13} className="text-amber-600 shrink-0" />}
                    <Badge value={m.state} label={t(`meet.state.${m.state}`)} />
                  </div>
                  <div className="text-xs text-muted mt-1 flex items-center gap-3 flex-wrap">
                    <span>{fmtDate(m.starts_at || m.created_at)}</span>
                    {m.deal_title && <span className="truncate">{m.deal_title}</span>}
                    {m.company_name && <span className="truncate">{m.company_name}</span>}
                    {m.has_transcript && (
                      <span className="inline-flex items-center gap-1">
                        <FileText size={12} /> {m.transcript_count}
                      </span>
                    )}
                    {m.open_action_items > 0 && (
                      <span className="inline-flex items-center gap-1 text-primary">
                        <ListChecks size={12} /> {t("meet.openItems", { count: m.open_action_items })}
                      </span>
                    )}
                  </div>
                  {m.summary && (
                    <p className="text-xs text-muted mt-1.5 line-clamp-2">{m.summary}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />
        </>
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
